import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Clapperboard } from "lucide-react";

import ViewContainer from "../../../../components/layout/view/ViewContainer";
import ViewHeader from "../../../../components/layout/view/ViewHeader";
import Text from "../../../../components/typography/Text";
import ContentGrid from "../../../../components/grids/content/ContentGrid";
import EmptyState from "../../../../components/feedback/EmptyState";
import Button from "../../../../components/buttons/Button";

import PopThumbnailCard from "../../../../components/cards/pops/PopThumbnailCard";

import { useUserPopsViews } from "../../saved/users/hooks/useUserPopsViews";
import { useHomePops } from "../hooks/useHomePops";

import "./HomePopsView.css";

const LIMIT = 10;

const validId = (value) => {
  const id = Number(value);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
};

const normalizeCount = (value, fallback = 0) => {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }

  const count = Number(value);
  return Number.isFinite(count) && count >= 0 ? count : fallback;
};

const normalizeViewResult = (result) => {
  const popId = validId(result?.popId ?? result?.pop_id);

  if (!popId) return null;

  const realViews = normalizeCount(
    result.realViews ?? result.real_views ?? result.views,
  );

  const demoViews = normalizeCount(result.demoViews ?? result.demo_views);

  const totalViews = normalizeCount(
    result.totalViews ?? result.total_views,
    realViews + demoViews,
  );

  return {
    popId,
    realViews,
    demoViews,
    totalViews,
  };
};

export default function HomePopsView() {
  const navigate = useNavigate();

  const { pops, loading, error, refresh } = useHomePops({
    limit: LIMIT,
  });

  const { getPopViewCounts } = useUserPopsViews();

  const [viewCounts, setViewCounts] = useState({});
  const getPopViewCountsRef = useRef(getPopViewCounts);

  useEffect(() => {
    getPopViewCountsRef.current = getPopViewCounts;
  }, [getPopViewCounts]);

  const popItems = useMemo(() => {
    if (!Array.isArray(pops)) return [];

    const seen = new Set();

    return pops
      .filter((pop) => {
        const popId = validId(pop?.popId);

        if (!popId || seen.has(popId)) return false;

        seen.add(popId);
        return true;
      })
      .slice(0, LIMIT);
  }, [pops]);

  useEffect(() => {
    if (!popItems.length) {
      setViewCounts({});
      return;
    }

    let active = true;

    const popIds = popItems.map((pop) => validId(pop.popId)).filter(Boolean);

    const loadViewCounts = async () => {
      try {
        const results = await getPopViewCountsRef.current(popIds);

        if (!active) return;

        if (!Array.isArray(results)) {
          throw new Error("Invalid homepage Pop view-count response.");
        }

        const nextCounts = {};

        results.forEach((result) => {
          const normalized = normalizeViewResult(result);

          if (!normalized) return;

          nextCounts[String(normalized.popId)] = normalized;
        });

        setViewCounts(nextCounts);
      } catch (viewError) {
        if (!active) return;

        console.error("Failed to load homepage Pop view counts:", viewError);
      }
    };

    void loadViewCounts();

    return () => {
      active = false;
    };
  }, [popItems]);

  const displayPops = useMemo(() => {
    return popItems.map((pop) => {
      const fetched = viewCounts[String(pop.popId)];

      const fallbackRealViews = normalizeCount(
        pop.realViews ?? pop.real_views ?? pop.views,
      );

      const fallbackDemoViews = normalizeCount(pop.demoViews ?? pop.demo_views);

      const fallbackTotalViews = normalizeCount(
        pop.totalViews ?? pop.total_views,
        fallbackRealViews + fallbackDemoViews,
      );

      const realViews = fetched ? fetched.realViews : fallbackRealViews;

      const demoViews = fetched ? fetched.demoViews : fallbackDemoViews;

      const totalViews = fetched ? fetched.totalViews : fallbackTotalViews;

      return {
        ...pop,
        views: totalViews,
        realViews,
        demoViews,
        totalViews,
      };
    });
  }, [popItems, viewCounts]);

  const hasPops = displayPops.length > 0;

  const handleOpenPop = (pop) => {
    const popId = validId(pop?.popId);

    if (!popId) return;

    navigate(`/pops/${popId}`);
  };

  const renderPop = (pop) => (
    <PopThumbnailCard
      pop={pop}
      mode="grid"
      onClick={() => handleOpenPop(pop)}
    />
  );

  return (
    <ViewContainer>
      <ViewHeader
        title="Pops"
        actions={
          <Link to="/pops" className="home-pops__view-all">
            View all
          </Link>
        }
      />

      {loading && !hasPops ? (
        <ContentGrid
          items={[]}
          loading
          columns={6}
          tabletColumns={4}
          mobileColumns={2}
          gap={16}
          skeletonCount={LIMIT}
        />
      ) : !hasPops ? (
        <EmptyState
          icon={<Clapperboard size={28} />}
          title={error ? "Unable to load Pops" : "No Pops available"}
          description={
            error ||
            "There are no public Pops available right now. Check back soon."
          }
          action={
            error ? (
              <Button
                value="Try Again"
                variant="primary-outlined"
                onClick={refresh}
              />
            ) : null
          }
        />
      ) : (
        <>
          {error && (
            <div className="home-pops__error" role="alert">
              <Text as="p" size="sm">
                {error}
              </Text>
            </div>
          )}

          <ContentGrid
            items={displayPops}
            columns={6}
            tabletColumns={4}
            mobileColumns={2}
            gap={16}
            showEmpty={false}
            renderItem={renderPop}
          />
        </>
      )}
    </ViewContainer>
  );
}
